import { useQuery } from "@tanstack/react-query";

import { isMainnet } from "#/config/web3/contractAddresses";
import { useBridgeFeatureFlag } from "#/hooks/featureFlags/useBridgeFeatureFlag";
import { defaultApiResponseHandler } from "#/services/kyan/kyan.utils";
import { useAccount } from "#/store/wallet/account/account";
import { isEqualAddress } from "#/utils/isEqualAddress";

import { fetchQuote } from "../bridge.service";
import { BungeeToken } from "../bridge.types";

export const useQuote = ({
  amount,
  inputToken,
  outputToken,
  refuel,
  isFormValid,
}: {
  amount: bigint;
  inputToken: BungeeToken;
  outputToken: BungeeToken;
  refuel?: boolean;
  isFormValid: boolean;
}) => {
  const { account } = useAccount();
  const { isBridgeDisabled } = useBridgeFeatureFlag();

  return useQuery({
    queryKey: [
      "fetchQuote",
      inputToken.address,
      inputToken.chainId,
      amount.toString(),
      account,
      refuel,
    ],
    queryFn: () =>
      defaultApiResponseHandler({
        serviceName: "Fetch Quote",
        payload: {
          userAddress: account!,
          originChainId: inputToken.chainId.toString(),
          inputToken: inputToken.address,
          inputAmount: amount.toString(),
          destinationChainId: outputToken.chainId.toString(),
          receiverAddress: account!,
          outputToken: outputToken.address,
          refuel: Boolean(refuel),
        },
        service: fetchQuote,
      }),
    select: (data) => data?.result,
    staleTime: (query) => {
      const expiry = query.state.data?.result?.autoRoute?.quoteExpiry;
      if (!expiry) return 0;

      const msUntilExpiry = expiry * 1000 - Date.now();
      return Math.max(msUntilExpiry, 0);
    },
    enabled: Boolean(
      account &&
      !isEqualAddress(inputToken.address, outputToken.address) &&
      isFormValid &&
      !isBridgeDisabled &&
      // only available on arbitrum mainnet
      isMainnet,
    ),
  });
};
